import { DownloadOutlined } from '@ant-design/icons';
import { Button, Empty, message, Modal, Spin, Typography } from 'antd';
import React, { useState } from 'react';
import {
  downloadMediaFile,
  useMediaFile,
} from '../../../../utils/api/medias/useMediaFile';
import {
  getPreviewKind,
  isTooLargeForPreview,
  MEDIA_PREVIEW_MAX_LABEL,
  toWindowsPath,
} from './mediaPath';

const PREVIEW_HEIGHT = '70vh';

// Aperçu d'un fichier de la bibliothèque, ouvert par double-clic.
const MediaPreviewModal = ({ file, onClose }) => {
  const kind = getPreviewKind(file?.media_format);
  const { url, error, isLoading } = useMediaFile(file?.path, {
    enabled: Boolean(kind) && !isTooLargeForPreview(file),
  });
  // Le back refuse aussi (413) si sa limite est plus basse que celle du front.
  const isTooLarge =
    isTooLargeForPreview(file) || error?.response?.status === 413;
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await downloadMediaFile(file.path, file.name);
    } catch (downloadError) {
      message.error(
        downloadError?.response?.status === 413
          ? `Fichier trop lourd pour Prométhée (plus de ${MEDIA_PREVIEW_MAX_LABEL}) : récupérez-le sur le disque média`
          : 'Erreur lors du téléchargement du fichier'
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const renderPreview = () => {
    if (isTooLarge) {
      return (
        <Empty
          description={
            <>
              <p>
                {`Aperçu impossible : le fichier fait ${file.size ?? `plus de ${MEDIA_PREVIEW_MAX_LABEL}`}, au-delà de la limite de ${MEDIA_PREVIEW_MAX_LABEL}. Récupérez-le directement sur le disque média :`}
              </p>
              <Typography.Text code copyable>
                {toWindowsPath(file.path)}
              </Typography.Text>
            </>
          }
        />
      );
    }
    if (!kind) {
      return (
        <Empty
          description={`Aperçu non disponible pour le format ${String(file.media_format).toUpperCase()}. Vous pouvez télécharger le fichier.`}
        />
      );
    }
    if (error) {
      return <Empty description="Impossible de charger l'aperçu du fichier" />;
    }
    if (isLoading) {
      return (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <Spin size="large" tip="Chargement de l'aperçu..." />
        </div>
      );
    }
    if (kind === 'image') {
      return (
        <img
          src={url}
          alt={file.name}
          style={{
            display: 'block',
            maxWidth: '100%',
            maxHeight: PREVIEW_HEIGHT,
            margin: '0 auto',
            objectFit: 'contain',
          }}
        />
      );
    }
    if (kind === 'video') {
      return (
        <video
          src={url}
          controls
          autoPlay
          style={{ display: 'block', width: '100%', maxHeight: PREVIEW_HEIGHT }}
        >
          <track kind="captions" />
        </video>
      );
    }
    return (
      <iframe
        src={url}
        title={file.name}
        style={{ width: '100%', height: PREVIEW_HEIGHT, border: 0 }}
      />
    );
  };

  return (
    <Modal
      open={Boolean(file)}
      title={file?.name}
      onCancel={onClose}
      width="80vw"
      destroyOnClose
      footer={[
        isTooLarge ? null : url ? (
          <Button
            key="download"
            icon={<DownloadOutlined />}
            href={url}
            download={file?.name}
          >
            Télécharger
          </Button>
        ) : (
          <Button
            key="download"
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            loading={isDownloading}
          >
            Télécharger
          </Button>
        ),
        <Button key="close" type="primary" onClick={onClose}>
          Fermer
        </Button>,
      ]}
    >
      {file && renderPreview()}
    </Modal>
  );
};

export default MediaPreviewModal;
